import React, { useMemo, useState, useEffect, useRef } from 'react';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { Button } from '@/components/ui/button';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import ScreenPasswordGate from '@/components/ScreenPasswordGate';
import MessagePostIt from '@/components/MessagePostIt';
import * as Sentry from "@sentry/react";
import { optimizeCloudinaryUrl } from '@/lib/utils';
import gifSrc from "@/assets/Instagram-heart-emotion.gif"
import { Heart, ChevronLeft, ChevronRight, Maximize } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useSettings } from '@/hooks/useSettings';
import { QRCodeCanvas } from 'qrcode.react';

interface MediaItem {
  id: string;
  type: 'media';
  secure_url: string;
  uploaded_by: string;
  votes_count: number;
  display_name?: string;
  avatar_url?: string;
  width?: number;
  height?: number;
  cdn_display_m?: string;
  description?: string;
  created_at?: string;
}

interface MessageItem {
  id: string;
  type: 'message';
  message: string;
  author_name?: string;
  author_avatar?: string;
  created_at?: string;
  user_id?: string;
}

type DisplayItem = MediaItem | MessageItem;
type MessageRow = Database['public']['Tables']['messages']['Row'];
type MediaWithVotesRow = Database['public']['Functions']['get_media_with_votes_filtered']['Returns'][number];
type UserDisplayInfoRow = Database['public']['Functions']['get_user_display_info']['Returns'][number];
type QrScreenMode = 'empty' | 'cycle' | null;

const MemoQRCodeCanvas = React.memo(QRCodeCanvas);

// Lightweight SVG heart with CSS animation (faster than Lottie)
const useInjectHeartKeyframes = () => {
  const injectedRef = useRef(false);
  useEffect(() => {
    if (injectedRef.current) return;
    const style = document.createElement('style');
    style.setAttribute('data-bk-heart', '1');
    style.textContent = `@keyframes bk-heart-pop { 0% { opacity: 0; transform: translateY(8px) scale(0.9); } 15% { opacity: 1; } 100% { opacity: 0; transform: translateY(-56px) scale(1.15); } }`;
    document.head.appendChild(style);
    injectedRef.current = true;
    return () => {
      try {
        document.head.removeChild(style);
      } catch {
        // Ignore cleanup races if the node is already gone.
      }
    };
  }, []);
};

const buildUserInfoMap = (rows: UserDisplayInfoRow[] | null | undefined): Record<string, UserDisplayInfoRow> =>
  (rows ?? []).reduce<Record<string, UserDisplayInfoRow>>((acc, user) => {
    acc[user.id] = user;
    return acc;
  }, {});

const mapMediaRpcRowToItem = (row: MediaWithVotesRow): MediaItem => ({
  id: row.id,
  type: 'media',
  secure_url: row.secure_url,
  uploaded_by: row.uploaded_by,
  votes_count: row.votes_count,
  width: row.width,
  height: row.height,
  cdn_display_m: row.cdn_display_m,
  description: row.description,
  created_at: row.created_at,
});

const mapMessageRowToItem = (row: MessageRow): MessageItem => ({
  id: row.id,
  type: 'message',
  message: row.message,
  created_at: row.created_at,
  user_id: row.user_id,
});

const HeartPop: React.FC<{ size?: number; rotateDeg?: number; delayMs?: number; gifSrc?: string; fill?: boolean }>
  = ({ size = 112, rotateDeg = 0, delayMs = 120, gifSrc, fill = false }) => {
    useInjectHeartKeyframes();
    // If a GIF is provided, we don't need keyframes; just render the image.
    if (gifSrc) {
      const px = `${size}px`;
      return (
        <div
          style={{
            width: fill ? '100%' : px,
            height: fill ? '100%' : px,
            transform: `rotate(${rotateDeg}deg)`,
            transformOrigin: 'center bottom',
            pointerEvents: 'none',
          }}
        >
          <img
            src={gifSrc}
            alt="heart animation"
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              willChange: 'transform,opacity',
            }}
          />
        </div>
      );
    }
    const px = `${size}px`;
    return (
      <div
        style={{
          width: fill ? '100%' : px,
          height: fill ? '100%' : px,
          transform: `rotate(${rotateDeg}deg)`,
          transformOrigin: 'center bottom',
          pointerEvents: 'none',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            bottom: 0,
            right: 0,
            animation: 'bk-heart-pop 900ms ease-out forwards',
            animationDelay: `${delayMs}ms`,
            willChange: 'transform,opacity',
          }}
        >
          <path
            fill="currentColor"
            d="M12 21s-6.716-4.297-9.333-7.23C.32 11.216.65 7.84 3.05 6.06 4.74 4.78 7.1 5.03 8.6 6.41L12 9.5l3.4-3.09c1.5-1.38 3.86-1.63 5.55-.35 2.4 1.78 2.73 5.156.383 7.71C18.716 16.703 12 21 12 21z"
          />
        </svg>
      </div>
    );
  };

export const GaleriaEnVivo = () => {
  const { albumId, albumSlug } = useAlbumContext();
  const { currentLanguage } = useTranslation();
  const { getBooleanSetting, getSetting } = useSettings();
  const QR_INTERSTITIAL_MS = 15000;
  const isDescriptionEnabled = getBooleanSetting('enable_single_image_description', false);
  const liveGalleryTitle = (getSetting('text_home_h1', '') || '').trim();
  const liveGalleryDescription = (getSetting('text_home_description', '') || '').trim();
  const qrUploadPrompt =
    currentLanguage === 'en-US'
      ? 'Upload your pictures'
      : currentLanguage === 'es-MX'
        ? 'Sube tus fotos'
        : 'Subí tus fotos';
  const qrUrl = useMemo(
    () => (albumSlug ? `https://app.bloomkeep.site/${albumSlug}?source=qr` : null),
    [albumSlug],
  );

  const [media, setMedia] = useState<MediaItem[]>([]);
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [displayItems, setDisplayItems] = useState<DisplayItem[]>([]);
  const [hasResolvedInitialContent, setHasResolvedInitialContent] = useState(false);
  const [qrScreenMode, setQrScreenMode] = useState<QrScreenMode>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const mediaLengthRef = useRef(0);
  const messagesSetRef = useRef(new Set<string>());
  const containerRef = useRef<HTMLDivElement>(null);

  const IMAGE_DELAY = 4000;
  const FADE_MS = 300;
  const HALF_FADE = Math.floor(FADE_MS / 2);
  const CHAR_DELAY_MS = 35; // Match MessagePostIt typing speed
  const MESSAGE_POST_DISPLAY_TIME = 2000; // Hold completed playback messages on screen for 2 seconds

  const intervalRef = useRef<ReturnType<typeof setTimeout>>();
  const qrInterstitialTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const currentIndexRef = useRef(0);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  // Simple in-memory image cache (optimizedUrl -> blob URL)
  const imgCacheRef = useRef<Map<string, string>>(new Map());
  const [, forceRerender] = useState(0);

  const MAX_CACHE_ITEMS = 48; // limit object URLs to avoid memory bloat

  const getKeepUrls = () => {
    // Protect current + a small window around it (prev/next 3)
    const keep = new Set<string>();
    if (media.length === 0) return keep;
    const windowSize = 3;
    for (let offset = -windowSize; offset <= windowSize; offset++) {
      const idx = (currentIndexRef.current + offset + media.length) % media.length;
      const m = media[idx];
      if (m) keep.add(makeUrl(m));
    }
    return keep;
  };

  const evictCacheIfNeeded = () => {
    const map = imgCacheRef.current;
    if (map.size <= MAX_CACHE_ITEMS) return;
    const keep = getKeepUrls();
    // Evict from the start of insertion order, skipping keep set
    for (const [url, objUrl] of map) {
      if (map.size <= MAX_CACHE_ITEMS) break;
      if (keep.has(url)) continue;
      try {
        URL.revokeObjectURL(objUrl);
      } catch {
        // Ignore invalid/revoked object URLs during cache eviction.
      }
      map.delete(url);
    }
  };

  const getCachedSrc = (url: string): string => {
    const cached = imgCacheRef.current.get(url);
    return cached !== undefined ? cached : url;
  };

  const prefetchToCache = async (urls: string[]) => {
    const uncached = urls.filter((u) => !imgCacheRef.current.has(u));
    if (uncached.length === 0) return;
    try {
      await Promise.all(
        uncached.map(async (u) => {
          const resp = await fetch(u);
          if (!resp.ok) return;
          const blob = await resp.blob();
          const objUrl = URL.createObjectURL(blob);
          imgCacheRef.current.set(u, objUrl);
        })
      );
      evictCacheIfNeeded();
      // Trigger paint so <img src> can swap to blob URLs when available
      forceRerender((v) => v + 1);
    } catch (e) {
      // Swallow cache errors; browser HTTP cache will still help
      console.warn('prefetchToCache error', e);
    }
  };

  const purgeFromCache = (urls: string[]) => {
    urls.forEach((u) => {
      const objUrl = imgCacheRef.current.get(u);
      if (objUrl) {
        try {
          URL.revokeObjectURL(objUrl);
        } catch {
          // Ignore invalid/revoked object URLs during cache cleanup.
        }
        imgCacheRef.current.delete(u);
      }
    });
  };

  const loadMedia = async (): Promise<MediaItem[] | null> => {
    if (!albumId) return [];
    try {
      const { data, error } = await supabase
        .rpc('get_media_with_votes_filtered', { p_album_id: albumId, p_exclude_flagged: true });
      if (error) {
        Sentry.captureException(error, {
          extra: { context: 'Error loading live gallery media' }
        });
        console.error('Error loading media:', error);
        return null;
      }
      const mediaData = (data ?? []).map(mapMediaRpcRowToItem);
      const userIds = [...new Set(mediaData.map((item) => item.uploaded_by).filter(Boolean))];
      if (userIds.length > 0) {
        const { data: userInfo, error: userError } = await supabase.rpc('get_user_display_info', { user_ids: userIds });
        if (!userError && userInfo) {
          const userMap = buildUserInfoMap(userInfo);
          return mediaData.map((item) => ({
            ...item,
            display_name: userMap[item.uploaded_by]?.display_name || 'Usuario anónimo',
            avatar_url: userMap[item.uploaded_by]?.avatar_url,
          }));
        }
      }
      return mediaData;
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'Error in loadMedia function' }
      });
      console.error('Error in loadMedia:', error);
      return null;
    }
  };

  const makeUrl = (m: MediaItem) => {
    if (m.cdn_display_m) return m.cdn_display_m;
    return optimizeCloudinaryUrl({
      originalUrl: m.secure_url,
      width: 1200,
    });
  };

  const loadMessages = async (): Promise<MessageItem[]> => {
    if (!albumId) return [];
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('album_id', albumId)
        .order('created_at', { ascending: false });

      if (error) {
        Sentry.captureException(error, {
          extra: { context: 'Error loading messages for live gallery' }
        });
        console.error('Error loading messages:', error);
        return [];
      }

      const messageData = (data ?? []).map(mapMessageRowToItem);
      const userIds = [...new Set(messageData.map((item) => item.user_id).filter(Boolean))];
      if (userIds.length > 0) {
        const { data: userInfo, error: userError } = await supabase.rpc('get_user_display_info', { user_ids: userIds });
        if (!userError && userInfo) {
          const userMap = buildUserInfoMap(userInfo);
          return messageData.map((item) => ({
            ...item,
            type: 'message' as const,
            author_name: userMap[item.user_id]?.display_name || 'Usuario anónimo',
            author_avatar: userMap[item.user_id]?.avatar_url,
          }));
        }
      }
      return messageData.map((item) => ({
        ...item,
        type: 'message' as const,
      }));
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'Error in loadMessages function' }
      });
      console.error('Error in loadMessages:', error);
      return [];
    }
  };

  const mergeItemsInOrder = (mediaList: MediaItem[], messageList: MessageItem[]): DisplayItem[] => {
    if (mediaList.length === 0 && messageList.length === 0) return [];
    if (mediaList.length === 0) return messageList;
    if (messageList.length === 0) return mediaList.map(m => ({ ...m, type: 'media' as const }));

    // Single-cycle sequence:
    // 1) Alternate media/message for min length
    // 2) Append remainder of longer list
    const combined: DisplayItem[] = [];
    const minLength = Math.min(mediaList.length, messageList.length);

    for (let i = 0; i < minLength; i++) {
      combined.push({ ...mediaList[i], type: 'media' as const });
      combined.push({ ...messageList[i], type: 'message' as const });
    }

    if (mediaList.length > minLength) {
      for (let i = minLength; i < mediaList.length; i++) {
        combined.push({ ...mediaList[i], type: 'media' as const });
      }
    }

    if (messageList.length > minLength) {
      for (let i = minLength; i < messageList.length; i++) {
        combined.push({ ...messageList[i], type: 'message' as const });
      }
    }

    return combined;
  };

  // Update displayItems whenever media or messages change
  useEffect(() => {
    const combined = mergeItemsInOrder(media, messages);
    setDisplayItems(combined);
    mediaLengthRef.current = combined.length;
  }, [media, messages]);

  const refreshMedia = async () => {
    const incoming = await loadMedia();
    if (!incoming) return;

    setMedia((prev) => {
      if (prev.length === 0) {
        if (incoming.length > 0) {
          // First load: set and prefetch all
          prefetchToCache(incoming.map(makeUrl));
        }
        return incoming;
      }

      const prevById = new Map(prev.map((p) => [p.id, p] as const));
      const incomingById = new Map(incoming.map((i) => [i.id, i] as const));

      // Purge any images that disappeared from the backend
      const removedItems = prev.filter((p) => !incomingById.has(p.id));
      if (removedItems.length) {
        purgeFromCache(removedItems.map(makeUrl));
      }

      // Merge updates for existing items and DROP those missing in incoming
      const mergedExisting = prev
        .map((p) => incomingById.get(p.id) ?? p)
        .filter((p) => incomingById.has(p.id));

      // Identify truly new items (not present before)
      const newOnes = incoming.filter((i) => !prevById.has(i.id));

      // Insert new items immediately after the current slide (do not change currentIndex)
      const insertPos = Math.min(currentIndexRef.current + 1, mergedExisting.length);
      const next = [
        ...mergedExisting.slice(0, insertPos),
        ...newOnes,
        ...mergedExisting.slice(insertPos),
      ];

      // Prefetch only the new ones
      if (newOnes.length) prefetchToCache(newOnes.map(makeUrl));

      // If the current slide was removed, move the index safely to a valid slide
      const currentPrev = prev[currentIndexRef.current];
      const currentStillExists = currentPrev && incomingById.has(currentPrev.id);
      if (!currentStillExists) {
        const safeIndex = Math.min(currentIndexRef.current, Math.max(0, next.length - 1));
        // Update outside of render stack but synchronously after state calc
        setCurrentIndex(safeIndex);
      }

      return next;
    });
  };

  useEffect(() => {
    mediaLengthRef.current = displayItems.length;
  }, [displayItems.length]);

  // No expand/collapse on this page

  const clearCycleQrTimeout = () => {
    if (qrInterstitialTimeoutRef.current) {
      clearTimeout(qrInterstitialTimeoutRef.current);
      qrInterstitialTimeoutRef.current = undefined;
    }
  };

  const refreshCycleContent = () => {
    void refreshMedia();
    void loadMessages().then(setMessages);
  };

  const startCycleQrInterstitial = () => {
    if (!qrUrl) return false;
    if (intervalRef.current) clearTimeout(intervalRef.current);
    clearCycleQrTimeout();

    setIsTransitioning(false);
    setQrScreenMode('cycle');
    refreshCycleContent();

    qrInterstitialTimeoutRef.current = setTimeout(() => {
      if (mediaLengthRef.current > 0) {
        clearCycleQrTimeout();
        setQrScreenMode(null);
        setCurrentIndex(0);
      } else {
        clearCycleQrTimeout();
        setQrScreenMode('empty');
      }
    }, QR_INTERSTITIAL_MS);

    return true;
  };

  const startSlideshow = () => {
    // Clear any previous timers before starting new ones
    if (intervalRef.current) clearTimeout(intervalRef.current);

    // Auto-advance slideshow with dynamic timing based on item type
    if (displayItems.length === 0 || qrScreenMode !== null) return;
    const currentItem = displayItems[currentIndex] || null;
    const duration = getDisplayDuration(currentItem);

    intervalRef.current = setTimeout(() => {
      nextImage('autoplay');
    }, duration);
  };

  const getDisplayDuration = (item: DisplayItem | null): number => {
    // Messages: typing duration + author fade-in + extra display time
    if (item && item.type === 'message') {
      const msg = item as MessageItem;
      const typingDuration = msg.message.length * CHAR_DELAY_MS;
      const authorFadeDuration = 500; // from CSS animation
      return typingDuration + authorFadeDuration + MESSAGE_POST_DISPLAY_TIME;
    }
    // Media: standard image delay
    return IMAGE_DELAY;
  };

  const nextImage = (source: 'autoplay' | 'manual' = 'manual') => {
    if (displayItems.length === 0 || isTransitioning || qrScreenMode !== null) return;
    setIsTransitioning(true); // start fade-out

    setTimeout(() => {
      const itemCount = mediaLengthRef.current || 1;
      const isLastItem = currentIndexRef.current >= itemCount - 1;

      if (source === 'autoplay' && isLastItem) {
        if (startCycleQrInterstitial()) {
          return;
        }

        refreshCycleContent();
        setCurrentIndex(0);
      } else {
        setCurrentIndex((prev) => (prev + 1) % itemCount);
      }

      // finish fade-in
      setTimeout(() => setIsTransitioning(false), HALF_FADE);
    }, HALF_FADE);
  };

  const prevImage = () => {
    if (displayItems.length === 0 || isTransitioning || qrScreenMode !== null) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(prev => (prev - 1 + (mediaLengthRef.current || 1)) % (mediaLengthRef.current || 1));
      setTimeout(() => setIsTransitioning(false), HALF_FADE);
    }, HALF_FADE);
  };

  // Initialize slideshow on mount
  useEffect(() => {
    let cancelled = false;

    setHasResolvedInitialContent(false);
    setQrScreenMode(null);
    setMedia([]);
    setMessages([]);
    setDisplayItems([]);
    setCurrentIndex(0);
    if (intervalRef.current) clearTimeout(intervalRef.current);
    clearCycleQrTimeout();

    if (!albumId) return () => {
      cancelled = true;
    };

    const initializeGallery = async () => {
      const [initialMediaResult, initialMessages] = await Promise.all([loadMedia(), loadMessages()]);
      if (cancelled) return;

      const initialMedia = initialMediaResult ?? [];
      const combined = mergeItemsInOrder(initialMedia, initialMessages);
      setMedia(initialMedia);
      setMessages(initialMessages);
      setDisplayItems(combined);
      mediaLengthRef.current = combined.length;
      setQrScreenMode(combined.length === 0 ? 'empty' : null);
      setHasResolvedInitialContent(true);
    };

    initializeGallery();

    return () => {
      cancelled = true;
    };
  }, [albumId]);

  // Start slideshow when displayItems first loads
  useEffect(() => {
    if (displayItems.length > 0 && qrScreenMode === null) {
      startSlideshow();
    }
    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [displayItems.length, qrScreenMode]);

  // Restart timer when currentIndex changes (after nextImage finishes)
  useEffect(() => {
    if (displayItems.length > 0 && !isTransitioning && qrScreenMode === null) {
      startSlideshow();
    }
    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [currentIndex, isTransitioning, qrScreenMode]);

  useEffect(() => {
    if (!hasResolvedInitialContent) return;

    if (displayItems.length === 0) {
      if (qrScreenMode === 'cycle') {
        clearCycleQrTimeout();
        setQrScreenMode('empty');
      } else if (qrScreenMode !== 'empty') {
        setQrScreenMode('empty');
      }
      return;
    }

    if (qrScreenMode === 'empty') {
      clearCycleQrTimeout();
      setQrScreenMode(null);
      setCurrentIndex(0);
    }
  }, [displayItems.length, hasResolvedInitialContent, qrScreenMode]);

  useEffect(() => {
    const cache = imgCacheRef.current;
    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      clearCycleQrTimeout();
      // Revoke cached blob URLs
      cache.forEach((objUrl) => URL.revokeObjectURL(objUrl));
      cache.clear();
    };
  }, []);
  // Subscribe to Supabase Realtime for media changes
  useEffect(() => {
    if (!albumId) return;
    // Use a channel for this album's media table, filtered by album_id
    const channel = supabase
      .channel('media-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'media',
          filter: `album_id=eq.${albumId}`,
        },
        (_payload: RealtimePostgresChangesPayload<Database['public']['Tables']['media']['Row']>) => {
          void refreshMedia();
        }
      );
    channel.subscribe();
    return () => {
      channel.unsubscribe();
    };
  }, [albumId]);

  // Subscribe to real-time message changes
  useEffect(() => {
    if (!albumId) return;
    const channel = supabase
      .channel(`messages-live-${albumId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `album_id=eq.${albumId}`,
        },
        async (payload: RealtimePostgresChangesPayload<MessageRow>) => {
          const { eventType, new: newRow, old: oldRow } = payload;
          if (eventType === 'INSERT') {
            try {
              // Fetch user info for new message
              const { data: userInfo } = await supabase.rpc('get_user_display_info', {
                user_ids: [newRow.user_id],
              });

              const userMap = buildUserInfoMap(userInfo);

              const newMessageItem: MessageItem = {
                ...mapMessageRowToItem(newRow),
                author_name: userMap[newRow.user_id]?.display_name || 'Usuario anónimo',
                author_avatar: userMap[newRow.user_id]?.avatar_url,
              };

              // Immediate display: insert directly after current slide in displayItems
              setDisplayItems((prev) => {
                if (prev.some((it) => it.type === 'message' && it.id === newRow.id)) return prev;
                const next = [...prev];
                const insertPos = Math.min(currentIndexRef.current + 1, next.length);
                next.splice(insertPos, 0, newMessageItem);
                mediaLengthRef.current = next.length;
                return next;
              });

              // Keep source of truth in messages (append at end, order doesn't matter; merge will rebuild)
              setMessages((prevMessages) => {
                if (prevMessages.some((m) => m.id === newRow.id)) return prevMessages;
                return [...prevMessages, newMessageItem];
              });
            } catch (error) {
              console.error('Error fetching user info for new message:', error);
              const fallbackItem: MessageItem = {
                ...mapMessageRowToItem(newRow),
                author_name: 'Usuario anónimo',
              };
              setDisplayItems((prev) => {
                if (prev.some((it) => it.type === 'message' && it.id === newRow.id)) return prev;
                const next = [...prev];
                const insertPos = Math.min(currentIndexRef.current + 1, next.length);
                next.splice(insertPos, 0, fallbackItem);
                mediaLengthRef.current = next.length;
                return next;
              });
              setMessages((prevMessages) => {
                if (prevMessages.some((m) => m.id === newRow.id)) return prevMessages;
                return [...prevMessages, fallbackItem];
              });
            }
          } else if (eventType === 'UPDATE') {
            setMessages((prevMessages) =>
              prevMessages.map((item) =>
                item.id === newRow.id
                  ? { ...item, ...newRow, type: 'message' }
                  : item
              )
            );
          } else if (eventType === 'DELETE') {
            setMessages((prevMessages) =>
              prevMessages.filter((m) => m.id !== oldRow.id)
            );
          }
        }
      );
    channel.subscribe();
    return () => {
      channel.unsubscribe();
    };
  }, [albumId]);

  useEffect(() => {
    if (displayItems.length > 0 && currentIndex >= displayItems.length) {
      setCurrentIndex(0);
    }
  }, [displayItems.length, currentIndex]);

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
    }
  };

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && document.fullscreenElement) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);



  const currentItem = displayItems[currentIndex] || null;
  const currentMedia = currentItem?.type === 'media' ? (currentItem as MediaItem) : null;
  const shouldShowQrScreen = hasResolvedInitialContent && qrScreenMode !== null;


  return (
    <ScreenPasswordGate scope="live-gallery">
      <div ref={containerRef} className="pointer-events-none h-screen overflow-hidden bg-black text-primary-foreground flex flex-col relative">
        {/* Fullscreen toggle button - only show when not in fullscreen */}
        {!isFullscreen && (
          <div className="pointer-events-auto absolute top-4 right-4 group">
            <button
              onClick={toggleFullscreen}
              className="bg-white hover:bg-gray-200 text-black rounded-lg p-2 transition-colors z-50 flex items-center justify-center"
            >
              <Maximize className="w-6 h-6" />
            </button>
            {/* Tooltip */}
            <div className="absolute top-full mt-2 right-0 bg-black text-white text-md w-96 px-3 py-2 rounded whitespace-normal opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              Modo Pantalla Completa. Presiona ESC para salir.
            </div>
          </div>
        )}
        {shouldShowQrScreen ? (
          <div className="flex-1 min-h-0 flex items-center justify-center px-4 pb-4">
            <div className="flex w-full max-w-3xl flex-col items-center justify-center gap-8 text-center">
              {liveGalleryTitle ? (
                <h2 className="max-w-3xl text-3xl font-semibold tracking-tight text-white md:text-5xl">
                  {liveGalleryTitle}
                </h2>
              ) : null}
              {qrUrl ? (
                <div className="animate-breathing rounded-[2rem] bg-white p-6 shadow-2xl md:p-8">
                  <MemoQRCodeCanvas
                    value={qrUrl}
                    size={280}
                    includeMargin={true}
                    level="M"
                    bgColor="rgba(0,0,0,0)"
                  />
                </div>
              ) : null}
              {qrUrl ? (
                <p className="max-w-2xl text-xl font-semibold tracking-tight text-white md:text-3xl">
                  {qrUploadPrompt}
                </p>
              ) : null}
              {liveGalleryDescription ? (
                <p className="max-w-2xl whitespace-pre-wrap text-lg font-light leading-relaxed text-white/80 md:text-2xl">
                  {liveGalleryDescription}
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex items-center justify-center px-4 pb-4">

            <div className="group relative w-full max-w-[90vw] min-w-0 flex items-center justify-center h-[calc(100dvh-160px)] md:h-[70vh]">
              <div
                className={`h-full w-full min-w-0 min-h-0 flex items-center justify-center transition-opacity duration-${FADE_MS} ease-in-out ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
              >
                <div className="relative h-full w-full flex items-center justify-center">
                  {displayItems.map((item, idx) => {
                    const isActive = idx === currentIndex;

                    if (item.type === 'message') {
                      const msg = item as MessageItem;
                      // Generate stable random rotation based on message ID
                      const seed = msg.id.charCodeAt(0) + msg.id.charCodeAt(Math.floor(msg.id.length / 2));
                      const rotation = -20 + ((seed % 41) / 40) * 40;

                      return (
                        <div
                          key={msg.id}
                          className={`absolute transition-opacity duration-${FADE_MS} ease-in-out ${isActive ? "opacity-100" : "opacity-0"} h-full w-full flex items-center justify-center`}
                          style={{ pointerEvents: "none" }}
                        >
                          <div className={`w-[60vw] h-[60vh] flex items-center justify-center scale-150`}>
                            <div className="scale-125">
                              <MessagePostIt
                                id={msg.id}
                                message={msg.message}
                                authorName={msg.author_name || 'Invitado'}
                                authorAvatar={msg.author_avatar}
                                dateCreated={msg.created_at || new Date().toISOString()}
                                isOwnMessage={false}
                                hideDate={true}
                                animate={isActive}
                                authorFontSize="1.5rem"
                                rotation={rotation}
                                enableListAnimation={false}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }

                    const m = item as MediaItem;
                    const hearts = Math.min(Math.max(m.votes_count || 0, 0), 12);
                    return (
                      <div
                        key={m.id}
                        className={`absolute transition-opacity duration-${FADE_MS} ease-in-out ${isActive ? "opacity-100" : "opacity-0"} h-full w-full flex flex-col items-center justify-center`}
                        style={{ pointerEvents: "none" }}
                      >
                        {/* User name and avatar - shows above image */}
                        {m.display_name && (
                          <div className="mb-4">
                            <div className="bg-white rounded-full px-4 py-2 flex items-center gap-2">
                              <img
                                src={m.avatar_url ?? ''}
                                alt={m.display_name ?? ''}
                                className="w-6 h-6 rounded-full object-cover border"
                              />
                              <span className="font-medium text-sm text-black">{m.display_name}</span>
                            </div>
                          </div>
                        )}
                        <div className="relative inline-block h-full w-auto max-w-full">
                          <img
                            src={getCachedSrc(makeUrl(m))}
                            alt="Galería en vivo"
                            className="h-full w-auto max-w-full object-contain rounded-lg md:shadow-xl"
                            onLoad={() => {
                              setLoadedImages(prev => new Set(prev).add(m.id));
                            }}
                          />
                          {/* Overlay hearts and counter - only for current image */}
                          {isActive && hearts > 0 && (
                            <>
                              <div className='pointer-events-none absolute bottom-2 right-2 z-30'>
                                <div className='relative'>
                                  <Heart className="w-8 h-8 md:w-10 md:h-10 fill-red-500 text-red-500" />
                                  <span className="absolute top-1 md:top-2 w-full text-center text-xs md:text-sm font-bold text-white">
                                    {m.votes_count}
                                  </span>
                                </div>
                              </div>
                              <div
                                key={`${m.id}-${m.votes_count}-${idx}`}
                                className="pointer-events-none absolute bottom-6 md:bottom-10 right-4 md:-right-10 z-30 text-red-500"
                              >
                                <div
                                  style={{
                                    height: 'clamp(160px, 34vh, 380px)',
                                    aspectRatio: '180 / 367',
                                    opacity: .7
                                  }}
                                >
                                  <HeartPop
                                    fill
                                    rotateDeg={Math.floor(Math.random() * 31 - 15)}
                                    delayMs={120}
                                    gifSrc={gifSrc}
                                  />
                                </div>
                              </div>
                            </>
                          )}

                          {/* Description overlay with gradient (if enabled and present) */}
                          {isActive && currentMedia && isDescriptionEnabled && m.description && loadedImages.has(m.id) && (
                            <>
                              <div className="pointer-events-none absolute bottom-0 left-0 w-full h-1/5 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-20" />
                              <div className="absolute left-4 right-4 bottom-3 z-30 text-white">
                                <p className="whitespace-pre-wrap break-words text-sm md:text-base">{m.description}</p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Navigation buttons - only show on hover */}
              <Button
                variant="secondary"
                size="sm"
                className="pointer-events-auto absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-80 hover:!opacity-100 transition-opacity duration-200"
                onClick={prevImage}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="pointer-events-auto absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-80 hover:!opacity-100 transition-opacity duration-200"
                onClick={() => nextImage('manual')}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>


            </div>
          </div>
        )}
      </div>
    </ScreenPasswordGate>
  );
};

export default GaleriaEnVivo;
